import { useState, useRef, useEffect, useCallback, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { toast } from 'react-toastify';
import { Bot, User, User2, Check, Lock, EyeOff, Eye, ChevronDown, ChevronUp, Edit2, Save, Ban, Unlock, Image as ImageIcon, Mic, Paperclip } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { parse, format, isToday, isYesterday } from 'date-fns';
import { updateClientNickname, blockContactByPhone, unblockContactByPhone } from '../services/api.ts';
import { getAvatarInitial, useClientAvatar } from '../hooks/useClientAvatar.ts';
import { formatMarkdownToSafeHtml } from '../../../../utils/html';

// Format time function for cards
const formatTime = (timeString?: string): string => {
  if (!timeString) return '';

  let messageDate: Date | null = null;

  // Try to parse as full datetime first (ISO format)
  const fullFormats = ["yyyy-MM-dd'T'HH:mm:ss.SSSxxx", "yyyy-MM-dd'T'HH:mm:ss"];
  for (const f of fullFormats) {
    const parsed = parse(timeString, f, new Date());
    if (!isNaN(parsed.getTime())) {
      messageDate = parsed;
      break;
    }
  }

  // Fallback to parsing timeString as Date
  if (!messageDate) {
    messageDate = new Date(timeString);
  }

  if (!messageDate || isNaN(messageDate.getTime())) {
    return timeString;
  }

  const timeFormatted = format(messageDate, 'HH:mm');

  if (isToday(messageDate)) {
    return timeFormatted;
  } else if (isYesterday(messageDate)) {
    return `ontem ${timeFormatted}`;
  } else {
    // For older messages, show date and time
    return `${format(messageDate, 'dd/MM/yyyy')} ${timeFormatted}`;
  }
};

const tagTokens = {
  human: { color: "#2563EB" },
  bot: { color: "#10B981" },
  done: { color: "#9CA3AF" },
};

const senderTokens = {
  client: { label: "Cliente", color: "#2563EB" },
  user: { label: "Atendente", color: "#0F766E" },
  bot: { label: "IA", color: "#D97706" },
} as const;

const lineClampStyle: CSSProperties = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
};

const formatPhone = (phone?: string): string => {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  if (!digits) return phone;

  // BR numbers: country 55 + area + 8/9 digits
  if (digits.startsWith("55") && digits.length >= 12) {
    const country = digits.slice(0, 2);
    const area = digits.slice(2, 4);
    const rest = digits.slice(4);
    const isMobile = rest.length >= 9;
    const prefix = isMobile ? rest.slice(0, 5) : rest.slice(0, 4);
    const suffix = isMobile ? rest.slice(5, 9) : rest.slice(4, 8);
    return `+${country} (${area}) ${prefix}-${suffix}`;
  }

  // Fallback: format last 8/9 digits
  if (digits.length >= 10) {
    const area = digits.slice(0, 2);
    const rest = digits.slice(2);
    const prefix = rest.length >= 9 ? rest.slice(0, 5) : rest.slice(0, 4);
    const suffix = rest.length >= 9 ? rest.slice(5, 9) : rest.slice(4, 8);
    return `(${area}) ${prefix}-${suffix}`;
  }

  return phone;
};

// Get display name with priority: apelido > chat_name > name
const normalizeName = (value?: string): string => {
  const cleaned = (value || '')
    .replace(/[\p{Extended_Pictographic}\uFE0F\u200D]/gu, '')
    .replace(/\s+/g, ' ')
    .trim();
  return cleaned;
};

const getDisplayName = (c: CardType): string => {
  const rawName = (c.apelido && c.apelido.trim() !== '') ? c.apelido : (c.chat_name || c.name || 'Cliente');
  const normalized = normalizeName(rawName);
  return normalized || 'Cliente';
};

const getLastMessageSender = (card: CardType): keyof typeof senderTokens | null => {
  if (card.lastMsgSender && senderTokens[card.lastMsgSender]) return card.lastMsgSender;
  if (card.lastMsgFromBot) return "bot";
  if (card.lastMsgFromMe) return "client";
  if (card.type === "bot") return "bot";
  return card.lastMsg ? "user" : null;
};

type LastMessageMediaType = "audio" | "image" | "media";

const getLastMessageMediaType = (card: CardType): LastMessageMediaType | null => {
  const rawType = String(card.lastMsgMediaType || "").toLowerCase();
  if (rawType.includes("audio")) return "audio";
  if (rawType.includes("image") || rawType.includes("foto") || rawType.includes("photo") || rawType.includes("img")) {
    return "image";
  }
  if (rawType) return "media";

  if (card.lastMsgAudioUrl) return "audio";
  if (card.lastMsgImageUrl) return "image";
  if (card.lastMsgMediaUrl) return "media";
  return null;
};

const getMediaLabel = (type: LastMessageMediaType | null): string => {
  if (type === "audio") return "Áudio";
  if (type === "image") return "Imagem";
  return "Mídia";
};

const formatName = (name?: string): { displayName: string; fullName: string } => {
  const defaultName = 'Cliente';
  const fullName = name || defaultName;
  const maxLength = 18;

  if (fullName.length <= maxLength) {
    return { displayName: fullName, fullName };
  }

  const truncated = fullName.substring(0, maxLength - 3) + '...';
  return { displayName: truncated, fullName };
};

// Avatar com pré-visualização ampliada
const UserAvatar = ({ id, name, type, photo }: { id: string | number; name?: string; type?: string; photo?: string }) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const { photo: resolvedPhoto, handleImageError } = useClientAvatar({ clienteId: id, photo });
  const tone =
    type === "humano"
      ? "bg-[#E6EBFF] text-[#2F3BBF]"
      : type === "bot"
      ? "bg-[#DCFCE7] text-[#0F766E]"
      : "bg-[#F3F4F6] text-[#4B5563]";

  const openPreview = () => {
    if (!resolvedPhoto) return;
    setIsPreviewOpen(true);
  };

  const closePreview = () => setIsPreviewOpen(false);

  const handleAvatarError = () => {
    setIsPreviewOpen(false);
    handleImageError();
  };

  const handleAvatarClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    openPreview();
  };

  const handleAvatarKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!resolvedPhoto) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      event.stopPropagation();
      openPreview();
    }
  };

  useEffect(() => {
    if (!isPreviewOpen || typeof document === "undefined") return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsPreviewOpen(false);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isPreviewOpen]);

  return (
    <>
      <div
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-base font-semibold select-none transition-transform duration-200 ease-out
        ${tone}
        ring-1 ring-white/80 shadow-[0_2px_8px_rgba(15,23,42,0.12)]
        ${resolvedPhoto ? "cursor-zoom-in hover:scale-105 focus-visible:scale-105" : ""}`
        }
        onClick={resolvedPhoto ? handleAvatarClick : undefined}
        onKeyDown={handleAvatarKeyDown}
        role={resolvedPhoto ? "button" : undefined}
        tabIndex={resolvedPhoto ? 0 : -1}
        aria-label={resolvedPhoto ? "Ver foto do cliente" : undefined}
      >
        {resolvedPhoto ? (
          <img
            src={resolvedPhoto}
            alt={name || "User"}
            className="w-full h-full object-cover rounded-full"
            onError={handleAvatarError}
          />
        ) : (
          getAvatarInitial(name)
        )}
      </div>

      {resolvedPhoto && isPreviewOpen && typeof document !== "undefined" && createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/70"
            onClick={(event) => {
              event.stopPropagation();
              closePreview();
            }}
          />
          <div className="relative z-10 flex flex-col items-end gap-3 px-4">
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                closePreview();
              }}
              className="rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-gray-700 shadow-md hover:bg-white"
            >
              Fechar
            </button>
            <img
              src={resolvedPhoto}
              alt={name || "User"}
              className="max-h-[80vh] max-w-[85vw] rounded-2xl shadow-2xl object-contain"
              onError={handleAvatarError}
            />
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

// Botão Finalizar (flutuante, topo-direita)
const FinalizeButton = ({ onClick }: { onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void }) => (
  <button
    className="absolute top-3 right-3 z-20 inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition"
    onClick={onClick}
    title="Finalizar atendimento"
    aria-label="Finalizar atendimento"
    type="button"
  >
    <Check size={16} />
  </button>
);

type FinishOptions = {
  reactivateBot?: boolean;
};

// CARD
interface ConversationCardProps {
  c: CardType;
  onClick?: (card: CardType) => void;
  onDetails?: (card: CardType) => void;
  onChangeType?: (card: CardType, type: string) => void;
  onFinish?: (card: CardType, options?: FinishOptions) => void;
  section: "humano" | "bot" | "done";
  botIsActive?: boolean;
  onUpdateNickname?: (id: string | number, nickname: string | null) => void;
}

const ConversationCard = ({
  c,
  onClick,
  onChangeType,
  onFinish,
  section,
  botIsActive = true,
  onUpdateNickname
}: ConversationCardProps) => {
  const [showFinishModal, setShowFinishModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(getDisplayName(c));
  const [isBlocking, setIsBlocking] = useState(false);
  const [blockedOverride, setBlockedOverride] = useState<boolean | null>(null);
  const editContainerRef = useRef<HTMLDivElement>(null);

  const isDoneSection = section === "done";
  const isBotType = c.type === "bot" || (!c.type && c.lastMsgFromBot);

  const baseTypeKey =
    isDoneSection
      ? "done"
      : isBotType
      ? "bot"
      : "human";

  const unreadCount = c.unreadCount ?? (c.unseen ? 1 : 0);
  const hasNewMessage = section === "humano" && hasUnreadManualMessage(c);
  const unreadLabel = `${unreadCount} ${unreadCount === 1 ? 'mensagem nova' : 'mensagens novas'}`;
  const baseTag = tagTokens[baseTypeKey];
  const isBlocked = blockedOverride ?? !!c.blocked;
  const lastMessageText = (c.lastMsg || "").trim();
  const lastMessageMediaType = getLastMessageMediaType(c);
  const showMediaFallback = !lastMessageText && (lastMessageMediaType || !!c.lastActivity);
  const mediaLabel = getMediaLabel(lastMessageMediaType);
  const lastMessagePreviewLabel = lastMessageText
    ? lastMessageText
    : showMediaFallback
    ? mediaLabel
    : "Sem mensagem";
  const lastMessageSender = (lastMessageText || showMediaFallback) ? getLastMessageSender(c) : null;
  const lastMessageToken = lastMessageSender ? senderTokens[lastMessageSender] : null;
  const lastMessagePreviewHtml = lastMessageText
    ? formatMarkdownToSafeHtml(lastMessageText, { style: 'whatsapp' })
    : "";
  const lastMessageTitle = lastMessageToken
    ? `${lastMessageToken.label}: ${lastMessagePreviewLabel}`
    : lastMessagePreviewLabel;

  const MediaIcon = lastMessageMediaType === "audio"
    ? Mic
    : lastMessageMediaType === "image"
    ? ImageIcon
    : Paperclip;

  const btnBase =
    "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-semibold border transition";
  const btnSendBot =
    `${btnBase} border-emerald-200 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-50`;
  const btnSendHuman =
    `${btnBase} border-indigo-200 text-indigo-700 hover:border-indigo-300 hover:bg-indigo-50`;
  const btnDisabled =
    `${btnBase} border-slate-200 text-slate-400 bg-slate-50 cursor-not-allowed`;

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
    setEditedName(getDisplayName(c));
  };

  const handleSaveClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      // Se estiver vazio, envia null para usar o nome original
      const nicknameToSave = editedName.trim() === '' ? null : editedName.trim();
      await updateClientNickname(c.id.toString(), nicknameToSave);
      onUpdateNickname?.(c.id, nicknameToSave);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating nickname:', error);
      toast.error('Erro ao atualizar o nome: ' + (error instanceof Error ? error.message : 'Erro desconhecido'));
    }
  };

  const handleBlockClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!c.phone) {
      toast.warning('Telefone não encontrado para este contato.');
      return;
    }
    setIsBlocking(true);
    try {
      if (isBlocked) {
        const removed = await unblockContactByPhone(c.phone);
        if (!removed) {
          toast.warning('Não foi possível reativar. Contato não encontrado na lista de bloqueados.');
        } else {
          setBlockedOverride(false);
        }
      } else {
        await blockContactByPhone(c.phone, getDisplayName(c));
        setBlockedOverride(true);
      }
    } catch (error) {
      console.error('Error toggling blocked contact:', error);
      toast.error('Erro ao atualizar o bloqueio do contato.');
    }
    setIsBlocking(false);
  };

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditedName(getDisplayName(c));
  }, [c]);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSaveClick(e as any);
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const closeFinishModal = () => setShowFinishModal(false);
  const openFinishModal = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setShowFinishModal(true);
  };
  const handleConfirmFinish = (options?: FinishOptions) => {
    onFinish?.(c, options);
    closeFinishModal();
  };
  const finishDescription =
    "A conversa sai do painel principal, mas volta ao receber ou enviar nova mensagem e continua disponível na página de conversas.";
  const isManualSection = section === "humano";

  // Detectar clique fora do componente de edição
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isEditing && editContainerRef.current && !editContainerRef.current.contains(event.target as Node)) {
        handleCancelEdit();
      }
    };

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing, handleCancelEdit]);

  return (
    <>
      <motion.div
      layout
      transition={{ type: "spring", damping: 26, stiffness: 240 }}
      initial={{ opacity: 0, scale: 0.98, y: 16 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.98, y: 24 }}
      className="group relative flex w-full min-h-[150px] cursor-pointer flex-col rounded-2xl border border-slate-200 bg-white p-5 font-urbanist shadow-[0_8px_24px_-20px_rgba(15,23,42,0.35)] transition hover:-translate-y-[1px] hover:shadow-[0_12px_28px_-20px_rgba(15,23,42,0.45)]"
      onClick={() => onClick && onClick(c)}
    >
      <span
        className="absolute left-0 top-0 h-full w-[3px] rounded-l-2xl"
        style={{ backgroundColor: baseTag.color }}
      />
      {hasNewMessage && (
        <span className="absolute left-[3px] top-0 h-full w-[5px] bg-amber-500" aria-hidden="true" />
      )}

      {section !== "done" && (
        <FinalizeButton onClick={openFinishModal} />
      )}

      <div className="flex items-start gap-3">
        <UserAvatar id={c.id} name={getDisplayName(c)} type={c.type} photo={c.photo} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-h-11 min-w-0 flex-col justify-center">
              <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
                {isEditing ? (
                  <div ref={editContainerRef} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      onKeyDown={handleKeyPress}
                      className="w-40 max-w-[180px] rounded-md border border-slate-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    />
                    <button
                      onClick={handleSaveClick}
                      className="p-1 rounded text-slate-500 hover:text-slate-700"
                      title="Salvar"
                      type="button"
                    >
                      <Save size={14} />
                    </button>
                  </div>
                ) : (
                  <>
                    <span
                      title={formatName(getDisplayName(c)).fullName}
                      className="truncate text-base font-bold leading-tight text-slate-900"
                    >
                      {formatName(getDisplayName(c)).displayName}
                    </span>
                    {hasNewMessage && (
                      <span
                        className="flex h-6 min-w-6 shrink-0 items-center justify-center rounded-full bg-emerald-600 px-1 text-xs font-bold text-white"
                        aria-label={unreadLabel}
                        title={unreadLabel}
                      >
                        {unreadCount > 99 ? '99+' : unreadCount}
                      </span>
                    )}
                    <div className="hidden group-hover:flex group-focus-within:flex items-center gap-1">
                      <button
                        onClick={handleEditClick}
                        className="shrink-0 p-1 rounded text-slate-400 hover:text-slate-600"
                        title="Editar nome"
                        type="button"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={handleBlockClick}
                        className={`shrink-0 p-1 rounded disabled:opacity-50 ${isBlocked ? "text-emerald-600 hover:text-emerald-700" : "text-slate-400 hover:text-red-600"}`}
                        title={isBlocked ? "Reativar IA neste cliente" : "Pausar IA neste cliente"}
                        type="button"
                        disabled={isBlocking}
                      >
                        {isBlocked ? <Unlock size={14} /> : <Ban size={14} />}
                      </button>
                    </div>
                  </>
                )}
              </div>
              {c.phone && (
                <div className="mt-0.5 text-xs leading-tight text-slate-500">{formatPhone(c.phone)}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-1 items-center py-4">
        <div className="flex w-full items-start gap-3 rounded-lg bg-[#E9EEF5] px-3 py-2.5">
          <div
            className={`min-w-0 flex-1 text-sm leading-relaxed ${hasNewMessage ? "font-bold text-slate-800" : "text-slate-600"}`}
            style={lineClampStyle}
            title={lastMessageTitle}
          >
            {lastMessageToken && (
              <span className="mr-1 font-semibold" style={{ color: lastMessageToken.color }}>
                {lastMessageToken.label}:
              </span>
            )}
            {lastMessageText ? (
              <span dangerouslySetInnerHTML={{ __html: lastMessagePreviewHtml }} />
            ) : showMediaFallback ? (
              <span className="inline-flex items-center gap-1.5 font-medium text-slate-500">
                <MediaIcon size={14} aria-hidden="true" />
                {mediaLabel}
              </span>
            ) : (
              <span className="text-slate-400">Sem mensagem</span>
            )}
          </div>
          <span className="self-end shrink-0 whitespace-nowrap text-[11px] font-medium text-slate-500">
            {formatTime(c.lastActivity?.toString())}
          </span>
        </div>
      </div>
      {section !== "done" && (
        <div className="flex justify-end">
          {section === "humano" && (
            <button
              className={botIsActive && !isBlocked ? btnSendBot : btnDisabled}
              onClick={e => {
                e.stopPropagation();
                if (botIsActive && !isBlocked) onChangeType?.(c, "bot");
              }}
              disabled={!botIsActive || !!isBlocked}
              title={
                !botIsActive ? "O Atendabot está desativado globalmente"
                : isBlocked ? "Contato com IA pausada para este cliente"
                : "Enviar para IA"
              }
              type="button"
            >
              {botIsActive && !isBlocked ? (
                <>
                  <Bot size={14} /> Enviar p/ IA
                </>
              ) : (
                <>
                  <Lock size={12} /> IA pausada
                </>
              )}
            </button>
          )}
          {section === "bot" && (
            <button
              className={btnSendHuman}
              onClick={e => { e.stopPropagation(); onChangeType?.(c, "humano"); }}
              type="button"
            >
              <User2 size={14} /> Assumir
            </button>
          )}
        </div>
      )}
    </motion.div>
    {typeof document !== "undefined" && createPortal(
      <AnimatePresence>
        {showFinishModal && (
          <motion.div
            key={`finish-modal-${c.id}`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center px-4"
            role="dialog"
            aria-modal="true"
            onClick={closeFinishModal}
          >
            <div
              className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
              aria-hidden="true"
            />
            <motion.div
              initial={{ translateY: -16, opacity: 0 }}
              animate={{ translateY: 0, opacity: 1 }}
              exit={{ translateY: 12, opacity: 0 }}
              transition={{ type: "spring", stiffness: 280, damping: 30 }}
              onClick={(event) => event.stopPropagation()}
              className="relative w-full max-w-md rounded-3xl border border-slate-200 bg-white/95 p-6 shadow-2xl shadow-slate-900/30"
            >
              <button
                type="button"
                onClick={closeFinishModal}
                className="absolute right-5 top-5 text-slate-400 hover:text-slate-600 transition"
                aria-label="Fechar confirmação"
              >
                &times;
              </button>
              <div className="space-y-4">
                <div className="space-y-1">
                  <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-slate-400">Confirmação</p>
                  <h3 className="text-xl font-bold text-slate-900">Finalizar conversa</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {finishDescription}
                  </p>
                </div>
                <div className="flex flex-col gap-3 pt-1">
                  {isManualSection ? (
                    <>
                      <button
                        onClick={() => handleConfirmFinish({ reactivateBot: true })}
                        type="button"
                        className="w-full rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition hover:shadow-emerald-500/50"
                      >
                        Finalizar e reativar IA
                      </button>
                      <button
                        onClick={() => handleConfirmFinish()}
                        type="button"
                        className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300"
                      >
                        Finalizar e manter IA desativada
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleConfirmFinish({ reactivateBot: true })}
                      type="button"
                      className="w-full rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-500 px-4 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/30 transition hover:shadow-blue-500/60"
                    >
                      Finalizar esta conversa
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>,
      document.body
    )}
  </>
  );
};

export type CardType = {
  id: string | number;
  name?: string;
  apelido?: string | null;
  chat_name?: string;
  type?: string;
  blocked?: boolean;
  phone?: string;
  lastMsg?: string;
  lastMsgMediaType?: string | null;
  lastMsgAudioUrl?: string | null;
  lastMsgImageUrl?: string | null;
  lastMsgMediaUrl?: string | null;
  lastMsgFromBot?: boolean;
  lastMsgFromMe?: boolean;
  lastMsgSender?: "bot" | "user" | "client";
  lastActivity?: string | number | Date;
  unseen?: boolean;
  unreadCount?: number;
  photo?: string;
};

type CardDateGroupKey = "today" | "yesterday" | "earlier";

const getCardActivityDate = (card: Pick<CardType, "lastActivity">): Date | null => {
  if (!card.lastActivity) return null;
  const date = new Date(card.lastActivity);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const isCardFromToday = (card: Pick<CardType, "lastActivity">): boolean => {
  const date = getCardActivityDate(card);
  return date ? isToday(date) : false;
};

const getCardDateGroup = (card: CardType): CardDateGroupKey => {
  const date = getCardActivityDate(card);
  if (date && isToday(date)) return "today";
  if (date && isYesterday(date)) return "yesterday";
  return "earlier";
};

const cardDateGroups: Array<{
  key: CardDateGroupKey;
  label: string;
  emptyLabel: string;
}> = [
  { key: "today", label: "Hoje", emptyLabel: "Nenhum atendimento de hoje." },
  { key: "yesterday", label: "Ontem", emptyLabel: "Nenhum atendimento de ontem." },
  { key: "earlier", label: "Anteriores", emptyLabel: "Nenhum atendimento anterior." },
];

const INITIAL_ROWS_TO_SHOW = 2;

const hasUnreadManualMessage = (card: CardType): boolean =>
  card.type !== "bot"
  && (Boolean(card.type) || !card.lastMsgFromBot)
  && (card.unreadCount ?? (card.unseen ? 1 : 0)) > 0;

const hasUnreadMessage = (card: CardType): boolean =>
  (card.unreadCount ?? (card.unseen ? 1 : 0)) > 0;

const sectionThemes = {
  humano: {
    title: "text-[#1E3A8A]",
    accent: "#2563EB",
    container: "bg-[#EEF2FF] border-[#C7D2FE]",
    badge: "bg-[#E0E7FF] text-[#1E3A8A]",
    iconBg: "bg-[#E0E7FF]",
    iconColor: "text-[#2563EB]",
    filterBg: "#EEF2FF",
  },
  bot: {
    title: "text-[#065F46]",
    accent: "#10B981",
    container: "bg-[#ECFDF5] border-[#A7F3D0]",
    badge: "bg-[#D1FAE5] text-[#065F46]",
    iconBg: "bg-[#D1FAE5]",
    iconColor: "text-[#10B981]",
    filterBg: "#ECFDF5",
  },
  done: {
    title: "text-slate-600",
    accent: "#9CA3AF",
    container: "bg-[#FAFAFB] border-[#E5E7EB]",
    badge: "bg-[#F3F4F6] text-slate-600",
    iconBg: "bg-[#F3F4F6]",
    iconColor: "text-slate-400",
    filterBg: "#F3F4F6",
  },
};

export function ConversationCardsSection({
  section,
  cards,
  onClickCard,
  onChangeType,
  onDetails,
  onFinish,
  botIsActive = true,
  onUpdateNickname
}: {
  section: "humano" | "bot" | "done";
  cards: CardType[];
  onClickCard?: (card: CardType) => void;
  onChangeType?: (card: CardType, type: string) => void;
  onDetails?: (card: CardType) => void;
  onFinish?: (card: CardType, options?: FinishOptions) => void;
  botIsActive?: boolean;
  onUpdateNickname?: (id: string | number, nickname: string | null) => void;
}) {
  const [showOnlyUnseen, setShowOnlyUnseen] = useState(false);
  const [rowsToShow, setRowsToShow] = useState<Record<CardDateGroupKey, number>>({
    today: INITIAL_ROWS_TO_SHOW,
    yesterday: INITIAL_ROWS_TO_SHOW,
    earlier: INITIAL_ROWS_TO_SHOW,
  });
  const [expandedGroups, setExpandedGroups] = useState<Partial<Record<CardDateGroupKey, boolean>>>({});
  const [columns, setColumns] = useState(() => {
    if (typeof window === "undefined") return 1;
    return window.innerWidth >= 1280 ? 3 : window.innerWidth >= 768 ? 2 : 1;
  });

  const sectionTitle = {
    humano: "Atendimento Manual",
    bot: "Atendabot",
    done: "Atendimentos Finalizados",
  }[section];
  const theme = sectionThemes[section];

  let displayCards: CardType[] = [];

  useEffect(() => {
    if (typeof window === "undefined") return;
    const getColumns = () => (window.innerWidth >= 1280 ? 3 : window.innerWidth >= 768 ? 2 : 1);
    const handleResize = () => setColumns(getColumns());
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (section === "humano") {
    if (botIsActive) {
      displayCards = cards.filter(
        c => c.blocked || c.type === "humano" || !c.type
      );
    } else {
      const botCards = cards.filter(
        c => !c.blocked && (c.type === "bot" || (!c.type && c.lastMsgFromBot))
      );
      const humanCards = cards.filter(
        c => c.blocked || c.type === "humano" || !c.type
      );
      const botIds = new Set(botCards.map(c => c.id));
      displayCards = [
        ...botCards,
        ...humanCards.filter(c => !botIds.has(c.id)),
      ];
    }
  } else if (section === "bot") {
    // Se bot está desativado, não mostra cards, mostra aviso.
    displayCards = botIsActive
      ? cards.filter(c => !c.blocked && (c.type === "bot" || (!c.type && c.lastMsgFromBot)))
      : [];
  } else {
    displayCards = cards.filter(c => !c.blocked && (c.type === section || !c.type));
  }

  // Filtro: só não lidos?
  const filteredCards = showOnlyUnseen
    ? displayCards.filter(hasUnreadManualMessage)
    : displayCards;

  // ORDENAÇÃO por lastActivity: mais recente primeiro
  const orderedCards = [...filteredCards].sort((a, b) => {
    const aTime = a.lastActivity ? new Date(a.lastActivity).getTime() : 0;
    const bTime = b.lastActivity ? new Date(b.lastActivity).getTime() : 0;
    return bTime - aTime;
  });
  const visibleCount = orderedCards.length;

  const cardsByDateGroup = orderedCards.reduce<Record<CardDateGroupKey, CardType[]>>((groups, card) => {
    groups[getCardDateGroup(card)].push(card);
    return groups;
  }, { today: [], yesterday: [], earlier: [] });
  const groupedCards = cardDateGroups.map((group) => ({
    ...group,
    cards: cardsByDateGroup[group.key],
  }));

  const emptyStateTitle = showOnlyUnseen
    ? "Nenhum não lido encontrado"
    : section === "humano"
      ? "Nenhum atendimento manual ativo no momento"
      : section === "bot"
        ? "Nenhum atendimento por IA ativo"
        : "Nenhum atendimento finalizado ainda";

  const emptyStateHint = section === "done"
    ? "Os atendimentos finalizados aparecerão aqui."
    : "Quando houver conversas, elas aparecerao aqui.";

  // ================= ATENDABOT DESATIVADO ==============
  if (section === "bot" && !botIsActive) {
    return (
      <div
        className="mb-20 mt-10 md:mt-14 px-4 py-12 rounded-2xl border border-red-200 shadow-lg bg-gradient-to-br from-[#fff0f3] to-[#ffe3e8] flex flex-col items-center justify-center"
      >
        <Bot size={48} className="mb-2 text-[#f43f5e]" />
        <h2 className="font-black text-2xl md:text-3xl text-[#f43f5e] mb-4 tracking-tight text-center">
          Atendabot desativado
        </h2>
        <p className="text-lg md:text-xl font-bold text-[#f43f5e] mb-1 text-center">
          O Atendabot está desativado globalmente no momento.
        </p>
        <p className="text-sm text-[#a51a35] text-center">
          Assim que o Atendabot for ativado, as conversas da IA aparecerao aqui.
        </p>
      </div>
    );
  }

  // ======================= DEFAULT =====================
  return (
    <div className="mb-16 mt-10 md:mt-12">
      <div className={`rounded-2xl border ${theme.container} p-4 md:p-5`}>
        <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-3">
            <h2 className={`inline-flex items-center gap-2 font-black text-lg md:text-xl tracking-tight ${theme.title}`}>
              {section === "humano" && <User size={19} className={theme.iconColor} aria-hidden="true" />}
              {section === "bot" && <Bot size={19} className={theme.iconColor} aria-hidden="true" />}
              {sectionTitle}
            </h2>
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${theme.badge}`}>
              {visibleCount}
            </span>
          </div>

          {section === "humano" && (
            <button
              className="inline-flex h-9 shrink-0 items-center gap-1.5 self-start rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600 transition hover:border-indigo-200 hover:text-indigo-700 md:self-auto"
              style={showOnlyUnseen ? { borderColor: theme.accent, color: theme.accent, backgroundColor: theme.filterBg } : undefined}
              onClick={() => setShowOnlyUnseen(v => !v)}
              type="button"
              aria-pressed={showOnlyUnseen}
              title="Mostrar apenas não lidos"
            >
              {showOnlyUnseen ? <EyeOff size={16} /> : <Eye size={16} />}
              {showOnlyUnseen ? "Apenas não lidos" : "Mostrar não lidos"}
              {showOnlyUnseen && (
                <span
                  className="ml-1 rounded-full px-2 text-[11px] font-bold text-white"
                  style={{ backgroundColor: theme.accent }}
                >
                  {displayCards.filter(hasUnreadManualMessage).length}
                </span>
              )}
            </button>
          )}
        </div>
        <AnimatePresence>
          {orderedCards.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 w-full min-h-[90px] text-slate-500">
              <div className={`mb-2 rounded-full flex items-center justify-center w-10 h-10 ${theme.iconBg}`}>
                {section === "humano" && <User2 size={20} className={theme.iconColor} />}
                {section === "bot" && <Bot size={20} className={theme.iconColor} />}
                {section === "done" && <Check size={20} className={theme.iconColor} />}
              </div>
              <span className="block mt-1 text-sm font-semibold text-center text-slate-600">
                {emptyStateTitle}
              </span>
              <span className="block text-[11px] text-slate-400 mt-0.5">
                {emptyStateHint}
              </span>
            </div>
          ) : (
            <div className="space-y-2">
              {groupedCards.map((group) => {
                if ((group.key === "yesterday" || group.key === "earlier") && group.cards.length === 0) {
                  return null;
                }

                const isExpanded = expandedGroups[group.key]
                  ?? (group.key === "today" && group.cards.length > 0);
                const cardsVisibleLimit = Math.max(1, rowsToShow[group.key] * Math.max(1, columns));
                const cardsToShow = group.cards.slice(0, cardsVisibleLimit);
                const hasMoreToShow = group.cards.length > cardsToShow.length;
                const remainingCardsCount = group.cards.length - cardsToShow.length;
                const hasLessToShow = cardsToShow.length > Math.max(1, columns);
                const unreadConversationsCount = group.cards.filter(hasUnreadMessage).length;
                const contentId = `${section}-${group.key}-cards`;

                return (
                  <section key={group.key} className="overflow-hidden">
                    <button
                      type="button"
                      className="inline-flex items-center gap-1.5 py-1.5 text-left transition hover:opacity-75 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                      style={{ outlineColor: theme.accent }}
                      onClick={() => setExpandedGroups((current) => ({
                        ...current,
                        [group.key]: !isExpanded,
                      }))}
                      aria-expanded={isExpanded}
                      aria-controls={contentId}
                    >
                      <span className={`text-xs font-extrabold uppercase tracking-wide ${theme.title}`}>{group.label}</span>
                      {isExpanded
                        ? <ChevronUp size={15} className={theme.iconColor} aria-hidden="true" />
                        : <ChevronDown size={15} className={theme.iconColor} aria-hidden="true" />}
                      <span className={`ml-0.5 inline-flex min-w-5 items-center justify-center rounded-full px-1.5 py-0.5 text-[10px] font-bold ${theme.badge}`}>
                        {group.cards.length}
                      </span>
                      {unreadConversationsCount > 0 && (
                        <span
                          className="inline-flex min-w-5 items-center justify-center rounded-full bg-orange-500 px-1.5 py-0.5 text-[10px] font-bold text-white"
                          aria-label={`${unreadConversationsCount} ${unreadConversationsCount === 1 ? 'conversa com mensagem não lida' : 'conversas com mensagens não lidas'} em ${group.label}`}
                          title={`${unreadConversationsCount} ${unreadConversationsCount === 1 ? 'conversa com mensagem não lida' : 'conversas com mensagens não lidas'}`}
                        >
                          {unreadConversationsCount}
                        </span>
                      )}
                    </button>

                    {isExpanded && (
                      <div id={contentId} className="pb-1 pt-2">
                        {group.cards.length === 0 ? (
                          <p className="py-3 text-center text-xs font-medium text-slate-500">{group.emptyLabel}</p>
                        ) : (
                          <>
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                              {cardsToShow.map((c) => (
                                <ConversationCard
                                  key={c.id}
                                  c={c}
                                  onClick={onClickCard}
                                  onDetails={onDetails}
                                  section={section}
                                  onChangeType={onChangeType}
                                  onFinish={onFinish}
                                  botIsActive={botIsActive}
                                  onUpdateNickname={onUpdateNickname}
                                />
                              ))}
                            </div>
                            {(hasMoreToShow || hasLessToShow) && (
                              <div className="mt-4 flex w-full flex-wrap justify-center gap-2">
                                {hasLessToShow && (
                                  <button
                                    className="flex items-center gap-1 rounded-md border bg-white/70 px-4 py-2 text-xs font-semibold transition hover:bg-white"
                                    style={{ borderColor: theme.accent, color: theme.accent }}
                                    onClick={() => setRowsToShow((current) => ({
                                      ...current,
                                      [group.key]: 1,
                                    }))}
                                    type="button"
                                  >
                                    <ChevronUp size={16} /> Ver menos
                                  </button>
                                )}
                                {hasMoreToShow && (
                                  <button
                                    className="flex items-center gap-1 rounded-md border bg-white/70 px-4 py-2 text-xs font-semibold transition hover:bg-white"
                                    style={{ borderColor: theme.accent, color: theme.accent }}
                                    onClick={() => setRowsToShow((current) => ({
                                      ...current,
                                      [group.key]: current[group.key] + 2,
                                    }))}
                                    type="button"
                                  >
                                    <ChevronDown size={16} /> Ver mais ({remainingCardsCount})
                                  </button>
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </section>
                );
              })}
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}


